import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Boxes, ClipboardCheck, Container, Droplets, Fuel, MapPinned, MessageCircle, PackageCheck, Repeat, ShieldCheck, Truck } from "lucide-react";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";
import IbcQuoteForm from "../components/IbcQuoteForm";
import RepeatOrderSection from "../components/RepeatOrderSection";
import { Reveal } from "../components/Motion";
import { WhatsAppIcon } from "../components/Brand";
import { waLink } from "../lib/site";

const CATEGORIES = [
  [ShieldCheck, "Tata Motors DEF", "Genuine Tata Motors Diesel Exhaust Fluid."],
  [Droplets, "Gulf AdBlue", "Gulf AdBlue for BS-VI diesel vehicles."],
  [Truck, "Eicher AdBlue", "AdBlue for Eicher trucks and buses."],
  [Fuel, "BharatBenz DEF", "DEF for BharatBenz commercial vehicles."],
  [Boxes, "DEF Buckets", "Bucket packs for workshops and small fleets."],
  [Container, "DEF IBC Tanks", "1000 L IBC tanks for high-volume users."],
];
const FIELDS = [
  { name: "name", label: "Full Name", required: true, attrs: { minLength: 2, autoComplete: "name" } },
  { name: "company", label: "Company Name", attrs: { autoComplete: "organization" } },
  { name: "phone", label: "Mobile Number", type: "tel", required: true, placeholder: "10-digit mobile number",
    attrs: { pattern: "(\\+?91)?[6-9][0-9]{9}", title: "Enter a valid 10-digit Indian mobile number (digits only)", inputMode: "tel", maxLength: 13 } },
  { name: "email", label: "Email Address", type: "email", attrs: { autoComplete: "email" } },
  { name: "product", label: "Product Selection", type: "select", required: true, full: true, options: CATEGORIES.map((c) => c[1]) },
  { name: "quantity", label: "Quantity Required", type: "number", required: true, placeholder: "e.g. 500", attrs: { min: 1, step: 1 } },
  { name: "quantity_unit", label: "Unit", type: "select", required: true, options: ["Litres", "Buckets", "IBC Tanks"] },
  { name: "city", label: "Delivery Location", required: true, full: true, placeholder: "Town / city, district" },
  { name: "message", label: "Additional Requirements", type: "textarea", full: true, placeholder: "Delivery schedule, frequency, vehicle type or any other details" },
];
const FACILITIES = [
  [ShieldCheck, "Genuine branded DEF", "Tata Motors, Gulf, Eicher and BharatBenz DEF / AdBlue sourced for commercial vehicle use."],
  [PackageCheck, "Buckets & IBC tanks", "Choose bucket packs for smaller needs or 1000 L IBC tanks for high-volume consumption."],
  [Fuel, "Own dispensing facilities", "Tata Motors and Gulf D.E.F. dispensing machines operated at MHM SUPER ADBLUE locations."],
  [MapPinned, "Shop network in West Bengal", "Multiple MHM SUPER ADBLUE shops to serve transporters and fleets across the region."],
  [ClipboardCheck, "Quotation for your location", "Share your delivery location and quantity — we confirm pricing and availability for you."],
  [MessageCircle, "Quick WhatsApp support", "Talk to our team directly on WhatsApp for bulk pricing and order follow-ups."],
];
const STEPS = ["Send your bulk enquiry", "We confirm pricing & availability", "Supply as agreed with you"];
const WA = "Hello MHM SUPER ADBLUE, I would like a bulk DEF / AdBlue quotation.";
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

const BulkHero = () => (
  <section data-testid="bulk-header" className="relative overflow-hidden bg-gradient-to-br from-navy via-[#0E2A63] to-[#1E4FC9] text-white">
    <div className="absolute inset-0 grid-lines opacity-60" />
    <div className="pointer-events-none absolute -left-32 -bottom-24 h-72 w-[140%] -rotate-3 rounded-[50%] border-t-[8px] border-brand/60" />
    <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-32 md:pt-40 lg:px-8 lg:pb-28">
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="eyebrow flex items-center gap-3 text-brand"><span className="h-px w-10 bg-brand" /> Bulk orders</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} data-testid="bulk-heading" className="mt-6 max-w-5xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
        Bulk DEF &amp; <span className="normal-case text-brand">AdBlue</span> Supply Across West Bengal
      </motion.h1>
      <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }} data-testid="bulk-subtitle" className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 md:text-lg">
        Reliable bulk supply solutions for transporters, fleet operators, workshops, dealerships and industrial customers.
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="mt-9 flex flex-col gap-3 sm:flex-row">
        <button onClick={() => scrollTo("bulk-form")} data-testid="bulk-hero-quote-button" className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 font-semibold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-dark">
          Request bulk quotation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
        <a href={waLink(WA)} target="_blank" rel="noreferrer" data-testid="bulk-hero-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1FAF55] px-7 py-4 font-semibold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#178F45]">
          <WhatsAppIcon /> Enquire on WhatsApp
        </a>
        <button onClick={() => scrollTo("repeat-order")} data-testid="bulk-hero-repeat-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-4 font-semibold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-white/10">
          <Repeat className="h-4 w-4 text-brand" /> Repeat order
        </button>
      </motion.div>
    </div>
  </section>
);

const Categories = ({ onPick }) => (
  <section data-testid="bulk-categories" className="bg-white py-16 md:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <p className="eyebrow text-brand">Product categories</p>
      <h2 className="mt-3 font-display text-4xl font-extrabold uppercase text-navy md:text-5xl">What we supply in bulk</h2>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CATEGORIES.map(([Icon, name, desc], i) => (
          <Reveal key={name} delay={(i % 3) * 0.06}>
            <button onClick={() => onPick(name)} data-testid={`bulk-category-${i}`} className="group flex h-full w-full items-start gap-4 rounded-[22px] border border-navy/10 bg-[var(--paper)] p-6 text-left transition-[border-color,transform] hover:-translate-y-1 hover:border-brand">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-navy text-brand transition-colors group-hover:bg-brand group-hover:text-white"><Icon className="h-5 w-5" /></span>
              <span className="flex-1">
                <span className="block font-display text-2xl font-bold uppercase text-navy">{name}</span>
                <span className="mt-1 block text-sm text-slate-600">{desc}</span>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand">Get bulk quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </span>
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Facilities = () => (
  <section data-testid="bulk-facilities" className="relative overflow-hidden bg-navy py-16 text-white md:py-24">
    <div className="pointer-events-none absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full border-[40px] border-brand/15" />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <p className="eyebrow text-brand">Bulk supply facilities</p>
      <h2 className="mt-3 max-w-3xl font-display text-4xl font-extrabold uppercase md:text-5xl">Built to keep your fleet moving</h2>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FACILITIES.map(([Icon, t, d], i) => (
          <Reveal key={t} delay={(i % 3) * 0.06}>
            <div className="h-full rounded-[22px] border border-white/10 bg-white/[0.04] p-6 transition-colors hover:border-brand/60">
              <Icon className="h-7 w-7 text-brand" />
              <h3 className="mt-5 font-display text-2xl font-bold uppercase">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <ol className="mt-12 grid gap-3 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-4 rounded-full border border-white/15 px-5 py-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand font-display text-lg font-bold">{i + 1}</span>
            <span className="text-sm font-semibold">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const FormSection = ({ initial }) => (
  <section id="bulk-form" className="bg-[var(--paper)] py-16 md:py-24">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-12 lg:px-8">
      <div className="lg:col-span-4">
        <p className="eyebrow text-brand">Bulk order enquiry</p>
        <h2 className="mt-3 font-display text-4xl font-extrabold uppercase text-navy md:text-5xl">Get your bulk quotation</h2>
        <p className="mt-4 text-slate-600">Fill in your requirement and our team will contact you with pricing and availability. Fields marked * are required.</p>
        <a href={waLink(WA)} target="_blank" rel="noreferrer" data-testid="bulk-side-whatsapp" className="mt-8 flex items-center gap-4 rounded-[22px] bg-navy p-5 text-white transition-transform hover:-translate-y-1">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#1FAF55]"><WhatsAppIcon className="h-6 w-6" /></span>
          <span><span className="block font-semibold">Prefer WhatsApp?</span><span className="text-sm text-slate-300">Send us your bulk requirement directly.</span></span>
        </a>
      </div>
      <Reveal delay={0.1} className="lg:col-span-8">
        <div className="clip-notch rounded-[26px] border border-navy/10 bg-white p-6 sm:p-10">
          <EnquiryForm type="bulk" testPrefix="bulk-order" fields={FIELDS} initial={initial} submitLabel="Request bulk quotation"
            waText={WA} successText="Your bulk enquiry has been received. Our team will contact you shortly with pricing and availability." />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function BulkOrders() {
  const [initial, setInitial] = useState(null);
  const pick = (product) => { setInitial({ product }); scrollTo("bulk-form"); };
  return (
    <>
      <Seo title="Bulk DEF & AdBlue Supply Across West Bengal" description="Bulk Tata Motors DEF, Gulf AdBlue, Eicher AdBlue and BharatBenz DEF supply in buckets and IBC tanks for transporters, fleet operators, workshops and industrial customers in West Bengal." />
      <BulkHero />
      <Categories onPick={pick} />
      <Facilities />
      <FormSection initial={initial} />
      <RepeatOrderSection />
      <section id="ibc-quote" data-testid="ibc-quote-section" className="border-t border-navy/10 bg-white py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <p className="eyebrow text-brand">IBC tanks · 1000 L</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold uppercase text-navy">IBC bulk quote</h2>
          <p className="mt-2 mb-8 max-w-2xl text-slate-600">Need several IBC tanks or staggered deliveries? Add each tank requirement with its delivery date and we'll send one combined quotation.</p>
          <IbcQuoteForm />
        </div>
      </section>
    </>
  );
}
